import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Dialog, useStyles, FilterContentLayout } from '@archipro-design/aria';
import * as S from './FilterDrawer.style';
import { pluralize } from '~/utils';
import { CategoryFilters } from './CategoryFilters';
import type { UseFilterStatesResult } from './use-magazine-filter-states';
import FilterDateDropdown from './FilterDropdown';
import { START_YEAR } from '../../configs/constants';
import {
    FilterMonthMobile,
    FilterYearMobile,
} from './FilterDrawer.mobile.style';

interface FilterDrawerMobileProps {
    controlledStates: UseFilterStatesResult;
    isOpen: boolean;
    toggleOpen: (val: boolean) => void;
}

const FilterDrawerMobile: React.FC<FilterDrawerMobileProps> = ({
    controlledStates,
    isOpen,
    toggleOpen,
}) => {
    const { css } = useStyles();
    const [open, setOpen] = React.useState(isOpen);

    React.useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const onCancelOrClose = () => {
        setOpen(false);
        toggleOpen(false);
    };

    return (
        <Dialog
            open={open}
            onOpen={() => setOpen(true)}
            onCancel={() => onCancelOrClose()}
            content={
                <FilterDialogContent
                    controlledStates={controlledStates}
                    onClose={() => onCancelOrClose()}
                />
            }
            design={{
                minHeight: `100vh`,
            }}
            variables={{
                rootBorderRadius: 0,
                rootWidth: '100%',
                rootPadding: 0,
                rootMaxWidth: `100vw`,
                rootMaxHeight: `100vh`,
                contentMargin: `0`,
            }}
            className={css(S.DialogContentWrapper)}
        />
    );
};
export default FilterDrawerMobile;

const yearRange = dayjs().year() - START_YEAR + 1;

const monthItems = Array.from({ length: 12 }, (_, k) => {
    return {
        key: k + 1,
        header: dayjs().month(k).format('MMMM'),
    };
});

const yearDropdownValues = Array.from({ length: yearRange }, (_, offset) => {
    const year = START_YEAR + offset;
    return { header: year.toString(), key: year };
}).reverse();

const FilterDialogContent: React.FC<
    Pick<FilterDrawerMobileProps, 'controlledStates'> & { onClose: () => void }
> = ({ controlledStates, onClose }) => {
    const { filterStates, applyFilterChange, clearAllFilters, updateFilter } =
        controlledStates;

    const [year, setYear] = useState<number | null>(filterStates.year);
    const [month, setMonth] = useState<number | null>(filterStates.month);

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const { css } = useStyles({ year, month });

    const [searchQueryYear, setSearchQueryYear] = useState(year?.toString());
    const [searchQueryMonth, setSearchQueryMonth] = useState(
        month ? monthItems[month - 1]?.header : '',
    );

    return (
        <FilterContentLayout
            onClose={onClose}
            title={'Filters'}
            design={{ display: 'block' }}
            submitButton={{
                children: `VIEW ${pluralize(
                    filterStates?.totalCount ?? 0,
                    'RESULT',
                )}`,
                onClick: () => {
                    applyFilterChange();
                    onClose();
                },
                color: 'dark',
            }}
            closeButton={{}}
            headerAction={{
                children: 'Clear all',
                onClick: () => {
                    if (!filterStates) return;
                    clearAllFilters();
                    onClose();
                },
            }}
            content={
                <div className="space-y-12 px-4">
                    <div className="mt-7.5">
                        <h4 className="mb-5.5 text-base font-medium leading-none">
                            {`MAGAZINES`}
                        </h4>
                        <CategoryFilters
                            onChange={(val) => {
                                updateFilter({
                                    categoryEnum: val,
                                });
                            }}
                            filterStates={filterStates}
                        />
                    </div>
                    <div>
                        <h4 className="mb-4 text-base font-medium leading-none">{`PUBLISHED`}</h4>
                        <FilterDateDropdown
                            key={1}
                            items={yearDropdownValues}
                            value={year === null ? null : year.toString()}
                            defaultValue={year}
                            className={css(FilterYearMobile)}
                            placeholder={'Select year'}
                            searchQuery={searchQueryYear}
                            onSearchChange={(data) => setSearchQueryYear(data)}
                            onChange={(value: number | undefined) => {
                                setYear(value ?? null);
                                if (value === undefined) {
                                    setMonth(null);
                                }
                                updateFilter(
                                    {
                                        year: value,
                                        month: undefined,
                                    },
                                    true,
                                );
                            }}
                        />
                        <FilterDateDropdown
                            key={2}
                            items={
                                currentYear === year
                                    ? monthItems.filter(
                                          (m) => m.key <= currentMonth,
                                      )
                                    : monthItems
                            }
                            defaultValue={month}
                            value={
                                month === null
                                    ? null
                                    : monthItems[month]?.header
                            }
                            className={css(FilterMonthMobile)}
                            placeholder={'Select month'}
                            searchQuery={searchQueryMonth}
                            onSearchChange={(data) => setSearchQueryMonth(data)}
                            onChange={(value: number | undefined) => {
                                setMonth(value ?? null);
                                updateFilter(
                                    {
                                        month: value,
                                    },
                                    true,
                                );
                            }}
                            disabled={year === null}
                        />
                    </div>
                </div>
            }
        />
    );
};
